<script>
// The UMD build bundles its dependencies and is ES5, which webpack 4 can parse
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer/dist/index.umd.js";

// Items closer than this are drawn as one group marker
const GROUP_RADIUS_METRES = 10;
const METRES_PER_DEGREE = 111320;
// Marker colours that flag a problem; a group takes the worst one present (first = worst)
const STATUS_SEVERITY = ["red", "vtred", "orangered", "orange"];
const GROUP_NEUTRAL_COLOR = "midnightblue";
const WATCHLIST_STAR =
  "M 35.000 15.000 L 39.702 18.472 L 37.853 12.927 L 42.608 9.528 L 36.763 9.573 L 35.000 4.000 L 33.237 9.573 L 27.392 9.528 L 32.147 12.927 L 30.298 18.472 L 35.000 15.000";
const CLUSTER_MAX_ZOOM = 15;
const CLUSTER_IMAGE_PATH = "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m";
const CLUSTER_IMAGE_SIZES = [53, 56, 66, 78, 90];

export default {
  name: "MapMarkerVT",
  props: {
    items: { type: Array },
    options: { type: Object },
    show_info_window: { type: Boolean },
    clusterMarkers: Boolean,
    no_clusters: Boolean,
    reset_zoom: {
      type: Boolean,
      default: true,
    },
    liveStream: Boolean,
    initial_lat_lng: Object,
    initial_zoom: Number,
  },
  watch: {
    items: function () {
      if (this.liveStream && this.has_rendred) return;
      this.updateMap();
      setTimeout(() => {
        this.has_rendred = true;
      }, 5000);
    },
    clusterMarkers: function () {
      // Only the clustering changed, so keep the user's current zoom and position
      !this.no_clusters && this.updateMap(true);
    },
    // Tenant config can load after the first render; redraw so grouping matches the tenant type
    groupNearbyItems: function () {
      this.markers && this.updateMap(true);
    },
  },
  computed: {
    // Grouping nearby markers is for tracking customers only, not powerdata
    groupNearbyItems: function () {
      const tenant = this.$store && this.$store.state.tenant_config;
      return !!tenant && tenant.type == "tracking";
    },
  },
  data: function () {
    return {
      markers: null,
      markerCluster: null,
      has_rendred: false,
    };
  },
  mounted() {
    this.$parent.getMap((map) => {
      map && this.items && this.initialize_marker(map);
    });
  },
  methods: {
    setMapOnNoLocation: function (map) {
      map.setCenter(this.initial_lat_lng);
      map.setZoom(this.initial_zoom);
    },
    setMapZoom: function (map, bounds) {
      if (this.items.length > 1) map.getZoom() > 15 ? map.setZoom(15) : map.fitBounds(bounds);
      if (this.items.length == 1) map.setZoom(12);

      if (map.getZoom() > 15) {
        map.setZoom(15);
      }
    },
    // Returns { itemIndex: [indices of all items within GROUP_RADIUS_METRES] } for spots shared by 2+ items
    groupOverlapping: function () {
      const result = {};
      // Journey markers are coloured by sequence (start/middle/end), so keep them separate
      if (!this.items || !this.groupNearbyItems || this.options.type == "marker") return result;
      // Bucket points into grid cells one radius wide, so each point only checks its 9 neighbouring cells
      const cells = {};
      const groups = [];
      this.items.forEach((elem, i) => {
        const lat = Number(elem[this.options.lat]);
        const lng = Number(elem[this.options.lng]);
        if (!lat || !lng) return;
        const y = lat * METRES_PER_DEGREE;
        const x = lng * METRES_PER_DEGREE * Math.cos((lat * Math.PI) / 180);
        const cx = Math.floor(x / GROUP_RADIUS_METRES);
        const cy = Math.floor(y / GROUP_RADIUS_METRES);
        const group = this.findNearbyGroup(cells, cx, cy, x, y);
        if (group) {
          group.indices.push(i);
          return;
        }
        const created = { x, y, indices: [i] };
        groups.push(created);
        const key = `${cx},${cy}`;
        if (!cells[key]) cells[key] = [];
        cells[key].push(created);
      });
      groups.forEach((group) => {
        if (group.indices.length > 1) group.indices.forEach((i) => (result[i] = group.indices));
      });
      return result;
    },
    findNearbyGroup: function (cells, cx, cy, x, y) {
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const group = (cells[`${cx + dx},${cy + dy}`] || []).find(
            (g) => Math.hypot(g.x - x, g.y - y) <= GROUP_RADIUS_METRES
          );
          if (group) return group;
        }
      }
      return null;
    },
    // How much an item adds to group/cluster counts: its asset count on location maps, otherwise 1
    itemWeight: function (elem) {
      return this.options.sumLabel ? Number(elem[this.options.label]) || 0 : 1;
    },
    // The same colour a single marker would get for this item
    itemColor: function (elem) {
      const type = elem[this.options.type];
      return this.options.type == "Type" ? decodeURIComponent(this.getPathAndColor(type).color) : this.getColor(type);
    },
    // getColor can return Vuetify theme names (e.g. "vtblue"), which SVG and CSS don't understand
    resolveColor: function (color) {
      const theme = this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.currentTheme;
      return theme && typeof theme[color] == "string" ? theme[color] : color;
    },
    groupColor: function (items, colors) {
      // Boolean statuses (e.g. HasIssue): the group is flagged if any item is
      const values = items.map((elem) => elem[this.options.type]);
      if (values.length && values.every((v) => typeof v == "boolean")) {
        return this.resolveColor(this.getColor(values.some(Boolean)));
      }
      const lower = colors.map((c) => `${c}`.toLowerCase());
      const worst = STATUS_SEVERITY.find((status) => lower.includes(status));
      if (worst) return colors[lower.indexOf(worst)];
      return colors.every((c) => c == colors[0]) ? colors[0] : GROUP_NEUTRAL_COLOR;
    },
    createGroupMarker: function (map, indices, position) {
      const items = indices.map((i) => this.items[i]);
      const colors = items.map((elem) => this.resolveColor(this.itemColor(elem)));
      const fill = encodeURIComponent(this.groupColor(items, colors));
      const total = items.reduce((sum, elem) => sum + this.itemWeight(elem), 0);
      const isWatchlisted = this.options.isWatchlisted && items.some((elem) => elem[this.options.isWatchlisted]);
      const count = `${total}`;
      const starWidth = isWatchlisted ? 16 : 0;
      const width = 24 + 8 * count.length + starWidth;
      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        zIndex: window.google.maps.Marker.MAX_ZINDEX,
        icon: {
          anchor: new window.google.maps.Point(0, 0),
          url: `data:image/svg+xml;utf-8,
                <svg width="${width + 6}" height="31" xmlns="http://www.w3.org/2000/svg">
                  <rect fill="${fill}" fill-opacity="0.35" rx="11" ry="11" x="6" y="0" height="25" width="${width}"/>
                  <rect fill="${fill}" fill-opacity="0.6" rx="11" ry="11" x="3" y="3" height="25" width="${width}"/>
                  <rect fill="${fill}" stroke="white" stroke-width="2" rx="11" ry="11" x="1" y="5" height="25" width="${
                    width - 2
                  }"/>
                  <text font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="bold" x="${
                    (width - starWidth) / 2 + 1
                  }" y="22" text-anchor="middle" fill="white">${count}</text>
                  ${
                    isWatchlisted
                      ? `<path fill="white" transform="translate(${width - 45}, 6)" d="${WATCHLIST_STAR}"/>`
                      : ""
                  }
                </svg>`,
        },
      });
      marker.weight = total;
      const entries = items.map((item, i) => ({ item, color: colors[i] }));
      const showGroup = () => {
        this.$emit("close_infowindow");
        this.$emit("show_group", { entries, total, position });
      };
      marker.addListener("mouseover", showGroup);
      marker.addListener("click", showGroup);
      return marker;
    },
    renderCluster: function ({ markers, position }) {
      // Sum each marker's weight, so group markers and asset counts are included
      const count = markers.reduce((sum, m) => sum + (m.weight != null ? m.weight : 1), 0);
      const index = Math.min(`${count}`.length, CLUSTER_IMAGE_SIZES.length);
      const size = CLUSTER_IMAGE_SIZES[index - 1];
      return new window.google.maps.Marker({
        position: position,
        zIndex: window.google.maps.Marker.MAX_ZINDEX + count,
        icon: {
          url: `${CLUSTER_IMAGE_PATH}${index}.png`,
          scaledSize: new window.google.maps.Size(size, size),
          anchor: new window.google.maps.Point(size / 2, size / 2),
        },
        label: { text: `${count}`, color: "black", fontSize: "11px", fontWeight: "bold" },
      });
    },
    clearMap: function () {
      this.$emit("close_group");
      if (this.markerCluster) {
        this.markerCluster.clearMarkers();
        this.markerCluster.setMap(null);
        this.markerCluster = null;
      }
      if (this.markers) {
        this.markers.map((e) => {
          if (e) {
            window.google.maps.event.clearInstanceListeners(e);
            e.setMap(null);
          }
        });
        this.markers = null;
      }
    },
    updateMap: function (keepView = false) {
      this.clearMap();
      this.$parent.getMap((map) => {
        this.initialize_marker(map, keepView);
      });
    },
    getColor: function (val) {
      if (typeof val == "string" && this.options.userType == "scv") {
        return val && val.toLowerCase().includes("installed")
          ? "primaryGrey"
          : val && val.toLowerCase().includes("registered")
            ? "lightCoral"
            : val && val.toLowerCase().includes("provisioned")
              ? "secondary"
              : "borderLight";
      } else if (typeof val == "string") {
        return val && val.toLowerCase().includes("friendly") && val.toLowerCase().trim() != "friendly"
          ? "lightCoral"
          : val && val == "outside"
            ? "orangered"
            : "midnightblue";
      } else if (typeof val === "object") {
        val = !val && this.options.userType == "scv" ? "Unknown" : val;
        const colorMap = {
          hasIssues: {
            alarm: "red",
            caution: "orange",
            check: "blue",
            noIssues: "green",
          },
          DeviceStatus: {
            Installed: "vtblue",
            Registered: "lightCoral",
            Provisioned: "secondary",
            Unknown: "borderLight",
            "Not Installed": "vtred",
          },
          default: "green",
        };

        const type = this.options.type || "default";
        const value = val ? val[0] : null;

        return value && colorMap[type] && colorMap[type][value] ? colorMap[type][value] : colorMap.default;
      } else if (typeof val == "boolean") {
        return val ? "lightcoral" : "midnightblue";
      } else {
        return "grey";
      }
    },
    getPathAndColor(value) {
      if (value && value.includes("Ground Mount")) {
        return {
          path: "M0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96z",
          color: "%231b428b",
        }; // Square
      } else if (value && value.includes("Pole Mount")) {
        return {
          path: "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z",
          color: "%23f7af5d",
        }; // Circle
      } else if (value && value.includes("Indoor")) {
        return {
          path: "M284.3 11.7c-15.6-15.6-40.9-15.6-56.6 0l-216 216c-15.6 15.6-15.6 40.9 0 56.6l216 216c15.6 15.6 40.9 15.6 56.6 0l216-216c15.6-15.6 15.6-40.9 0-56.6l-216-216z",
          color: "%23bbc492",
        }; // Rhombus
      } else {
        return {
          path: "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z",
          color: "grey",
        }; // Default Circle
      }
    },
    initialize_marker: function (map, keepView = false) {
      // Multiple queued getMap callbacks can call this back-to-back; remove any previous render first
      this.clearMap();
      let bounds = new window.google.maps.LatLngBounds();
      const groups = this.groupOverlapping();
      this.markers =
        this.items &&
        this.items.map((elem, i) => {
          let id =
            elem[this.options.id] && elem[this.options.id].toString() ? elem[this.options.id].toString() : "un_" + i;
          let label = this.options.label
            ? elem[this.options.label]
              ? `${elem[this.options.label]}`
              : this.options.label
                ? ""
                : "0"
            : this.options.label == ""
              ? null
              : "";
          let isWatchlisted = elem[this.options.isWatchlisted];
          let type = elem[this.options.type];
          var temp = label || "1";
          let lat = elem[this.options.lat];
          let lng = elem[this.options.lng];
          if (lat && lng) {
            let position = new window.google.maps.LatLng(lat, lng);
            if (this.items.length != 1) bounds.extend(position);
            // Items sharing a location render as one group marker, drawn for the first item of the group
            if (groups[i]) {
              return groups[i][0] == i ? this.createGroupMarker(map, groups[i], position) : null;
            }
            var length = label ? temp.length : 0;
            var width =
              this.options.userType == "scv"
                ? 4
                : length == 0
                  ? 18
                  : isWatchlisted && length == 1
                    ? 45
                    : isWatchlisted
                      ? 30
                      : 22;
            if (length > 1)
              width =
                length == 2
                  ? isWatchlisted
                    ? width + 15
                    : width + 10
                  : isWatchlisted
                    ? width + 20
                    : width + 10 * (length - 2);

            let icon;
            if (this.options.type == "marker") {
              icon = {
                path: "M0-48c-9.8 0-17.7 7.8-17.7 17.4 0 15.5 17.7 30.6 17.7 30.6s17.7-15.4 17.7-30.6c0-9.6-7.9-17.4-17.7-17.4z",
                fillColor: `${i == 0 ? "#e14615" : i == this.items.length - 1 ? "#bbc492" : "#1b428b"}`,
                fillOpacity: i == 0 || i == this.items.length - 1 ? 0.8 : 0.65,
                strokeColor: "#a1c7e2",
                scale: i == 0 || i == this.items.length - 1 ? 0.6 : 0.5,
                strokeWeight: 1,
              };
            } else if (this.options.type == "Type") {
              icon = {
                anchor: new window.google.maps.Point(0, 0),
                url: `
                data:image/svg+xml;utf-8,
                <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 512 512">
                  <path fill="${this.getPathAndColor(type).color}" d="${this.getPathAndColor(type).path}"/>
                  </svg>
                `,
              };
            } else {
              icon = {
                anchor: new window.google.maps.Point(0, 0),
                url: `data:image/svg+xml;utf-8,
                      <svg width="${width}" height="${
                        this.options.userType == "scv" ? 4 : length == 0 ? 18 : 25
                      }" xmlns="http://www.w3.org/2000/svg">
                        <rect fill="${this.getColor(type)}" fill-opacity="0.8" rx="11" ry="11" stroke-width="${
                          length == 1 && !isWatchlisted ? 2 : 0
                        }" stroke="${
                          length == 1 && !isWatchlisted ? "white" : ""
                        }" y="0" x="0" height="100%" width="100%"/>
                         <text font-family="Arial, Helvetica, sans-serif" font-size="13" x="${
                           length >= 3 ? 5 : length == 1 && isWatchlisted ? 10 : 7
                         }" y="18" fill="white">${label ? label : ""}</text>
                        ${
                          isWatchlisted
                            ? '<path  fill="white" d="M 35.000 15.000 L 39.702 18.472 L 37.853 12.927 L 42.608 9.528 L 36.763 9.573 L 35.000 4.000 L 33.237 9.573 L 27.392 9.528 L 32.147 12.927 L 30.298 18.472 L 35.000 15.000" />'
                            : ""
                        }
                      </svg>`,
              };
            }
            let marker;
            marker = new window.google.maps.Marker({
              position: position,
              map: map,
              icon: icon,
            });
            marker.weight = this.itemWeight(elem);
            if (this.items.length == 1 && !keepView) {
              map.setCenter(marker.getPosition());
            }
            marker.addListener("click", () => {
              if (this.show_info_window) this.$emit("marker_clicked", [id, marker.getPosition()]);
              else {
                map.setCenter(marker.getPosition());
                this.$emit("marker_click", marker.getPosition());
              }
            });
            marker.addListener("mouseover", () => {
              this.$emit("close_group");
              if (this.show_info_window) {
                this.$emit("close_infowindow");
                this.$emit("show_infowindow", [id, marker.getPosition()]);
              }
            });
            // marker.addListener("mouseout", () => {
            //   if (this.show_info_window) this.$emit("close_infowindow");
            // });
            return marker;
          }
          return null;
        });

      if (!this.no_clusters && this.clusterMarkers) {
        // Items without a location have no marker, so drop the nulls
        this.markerCluster = new MarkerClusterer({
          map: map,
          markers: (this.markers || []).filter(Boolean),
          algorithm: new SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM }),
          renderer: { render: this.renderCluster },
        });
      }
      if (keepView) {
        // do nothing
      } else if (this.items.length == 0 && this.reset_zoom) {
        this.setMapOnNoLocation(map);
      } else if (!this.reset_zoom) {
        // do nothing
      } else this.setMapZoom(map, bounds);
    },
  },
  beforeDestroy() {
    this.clearMap();
  },
  render() {
    return null;
  },
};
</script>
